import { useHistory } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { generateMediaURL } from 'helpers/generateMediaURL';
import { formatDate } from 'helpers/formatDate';
import { pathnames } from 'constants/pathnames';
import { ImageComponent } from 'components/Image';
import styles from 'pages/News/NewsCard/NewsCard.scss';

export const NewsCard = ({ news: { id, title, description, publicationDate, media, views, tags, author } }) => {
  const { newsPage } = pathnames;
  const { push } = useHistory();

  const openArticle = () => push(`${newsPage}/${id}`);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      openArticle();
    }
  };

  // The description's line-clamp depends on whether the title actually wrapped
  // to two lines, which can only be known after layout — hence the measurement.
  const titleRef = useRef(null);
  const [isTitleTwoLines, setIsTitleTwoLines] = useState(false);

  useEffect(() => {
    const titleEl = titleRef.current;
    if (!titleEl) return;

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(titleEl).lineHeight);
      setIsTitleTwoLines(titleEl.offsetHeight > lineHeight * 1.5);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(titleEl);
    return () => observer.disconnect();
  }, [title]);

  return (
    <article className={styles.article}>
      <section className={styles.characteristic}>
        <ul className={styles.tags}>
          {tags.map(({ id, tag }) => (
            <li className={styles.tag} key={id}>
              {tag.trim()}
            </li>
          ))}
        </ul>
        {author && <p className={styles.author}>by {author.trim()}</p>}
      </section>
      <section
        className={styles.card}
        onClick={openArticle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role='button'
        aria-label={`Open article: ${title}`}
      >
        <ImageComponent
          src={generateMediaURL(media?.[0].url) || 'https://placehold.co/282'}
          alt={media?.[0].alt || 'Cover placeholder'}
          placeholder={media?.[0].placeholder}
          className={styles.cover}
          fit='contain'
        />
        <section className={styles.info}>
          <h3 ref={titleRef} className={styles.title}>{title.trim()}</h3>
          <p className={cn(styles.description, { [styles.descriptionShort]: isTitleTwoLines })}>{description.trim()}</p>
          <div className={styles.additionalInfo}>
            <span className={styles.date}>{formatDate(publicationDate)}</span>
            <div className={styles.views}>
              <span className={styles.viewsIcon} />
              {views}
            </div>
          </div>
        </section>
      </section>
    </article>
  );
};
